import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Grid,
  Button,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  ContentCopy as ContentCopyIcon,
  QrCode as QrCodeIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import {
  GET_SOCIAL_SHARE_SETTINGS,
  GET_EVENT_SHARE_DETAILS,
  GET_LINK,
} from "./graphql/queries";
import { getBranding } from "./utils/branding";

const SocialSharePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);

  // Extract eventId and role from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get("eventId");
  const urlRole = searchParams.get("role");

  // Get registration data
  const registrationData = location.state?.registrationData || {};
  const registrationRole = registrationData?.role;

  // Use the role from registration data if available, otherwise use the URL parameter or default to 'attendee'
  const role = registrationRole || urlRole || "attendee";

  // Create the direct sharing URL for social media
  const socialShareUrl = `${window.location.origin}/events/${eventId}/${role}`;

  // Fetch social share settings
  const {
    loading: settingsLoading,
    error: settingsError,
    data: settingsData,
  } = useQuery(GET_SOCIAL_SHARE_SETTINGS, {
    variables: { eventId },
    skip: !eventId,
  });

  // Fetch event details
  const {
    loading: eventLoading,
    error: eventError,
    data: eventData,
  } = useQuery(GET_EVENT_SHARE_DETAILS, {
    variables: { eventId },
    skip: !eventId,
  });

  const loading = settingsLoading || eventLoading;
  const error = settingsError || eventError;

  // Combine data from both queries
  const settings = settingsData?.socialShareSettings;
  const event = eventData?.event;

  // Prepare share content based on settings and event data
  const shareTitle = settings?.shareTitle || event?.name || "Join this event";
  const shareDescription =
    settings?.shareDescription ||
    event?.description ||
    "Register now for this event!";
  const shareImage = settings?.bannerImage || event?.bannerImage;
  const shareUrl = socialShareUrl;

  // Get enabled platforms and sort by display order
  const platforms =
    settings?.platforms
      ?.filter((p) => p.enabled)
      ?.sort((a, b) => a.displayOrder - b.displayOrder) || [];

  // Generate share text for each platform or use default
  const getShareContent = (platform) => {
    const platformData = platforms.find((p) => p.name === platform);
    let text =
      platformData?.customMessage ||
      settings?.defaultMessage ||
      `I'm attending ${shareTitle}. ${shareDescription}`;

    // Add URL if includeLink is true or not specified
    if (settings?.includeLink !== false) {
      text += ` ${shareUrl}`;
    }

    return text;
  };
  console.log(shareUrl);
  // Generate share URLs for each platform
  const socialLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}&quote=${encodeURIComponent(getShareContent("facebook"))}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      getShareContent("twitter")
    )}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
      shareUrl
    )}&title=${encodeURIComponent(shareTitle)}`,
    whatsapp: `whatsapp://send?text=${encodeURIComponent(
      getShareContent("whatsapp")
    )}`,
    email: `mailto:?subject=${encodeURIComponent(
      settings?.platforms?.find((p) => p.name === "email")?.customSubject ||
        shareTitle
    )}&body=${encodeURIComponent(getShareContent("email"))}`,
  };

  // Handle copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(getShareContent("facebook"));
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Navigate back
  const handleBack = () => {
    navigate(-1);
  };

  // Handle social media click
  const handleSocialClick = (platform) => {
    window.open(socialLinks[platform], "_blank", "width=600,height=400");
  };

  if (!eventId) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h5" color="error" gutterBottom>
          Invalid URL
        </Typography>
        <Typography variant="body1" gutterBottom>
          This page requires an event ID parameter.
        </Typography>
        <Button
          variant="contained"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading share information. Please try again later.
        </Alert>
        <Button
          variant="contained"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  // If sharing is disabled for this event, show message
  if (settings && !settings.enabled) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          Social Sharing Not Available
        </Typography>
        <Typography variant="body1" gutterBottom>
          Social sharing is not available for this event.
        </Typography>
        <Button
          variant="contained"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Return to Event
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Share - ${shareTitle}`}</title>
        <meta name="description" content={shareDescription} />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        {shareImage && <meta property="og:image" content={shareImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        {shareImage && <meta name="twitter:image" content={shareImage} />}
      </Helmet>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              Let your network know you're attending!
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Easily share on social media with the links below.
          </Typography>

          <Card sx={{ mb: 4 }}>
            {shareImage ? (
              <CardMedia
                component="img"
                height="180"
                image={shareImage}
                alt={shareTitle}
              />
            ) : (
              <Box
                sx={{
                  height: 180,
                  bgcolor: "primary.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {event?.logoUrl ? (
                  <img
                    src={event.logoUrl}
                    alt={shareTitle}
                    style={{ maxHeight: "80%", maxWidth: "80%" }}
                  />
                ) : (
                  <Typography variant="h4" color="white">
                    {shareTitle}
                  </Typography>
                )}
              </Box>
            )}
            <CardContent>
              <Typography gutterBottom variant="h6" component="div">
                {shareTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {shareDescription}
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ display: "flex", mb: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              value={getShareContent("facebook")}
              InputProps={{ readOnly: true }}
              sx={{ mr: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleCopy}
              startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Select where to share
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              {
                id: "facebook",
                name: "Facebook",
                icon: <FacebookIcon />,
                color: "#1877F2",
              },
              {
                id: "twitter",
                name: "X (Twitter)",
                icon: <TwitterIcon />,
                color: "#000000",
              },
              {
                id: "linkedin",
                name: "LinkedIn",
                icon: <LinkedInIcon />,
                color: "#0A66C2",
              },
              {
                id: "whatsapp",
                name: "WhatsApp",
                icon: <WhatsAppIcon />,
                color: "#25D366",
              },
              {
                id: "email",
                name: "Email",
                icon: <EmailIcon />,
                color: "#EA4335",
              },
            ]
              .filter((platform) => {
                // Only show platforms that are enabled in settings
                const platformEnabled = platforms.some(
                  (p) => p.name === platform.id
                );
                // If no platforms are configured yet, show all
                return platforms.length === 0 || platformEnabled;
              })
              .map((platform) => (
                <Grid item xs={6} sm={4} key={platform.id}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={platform.icon}
                    onClick={() => handleSocialClick(platform.id)}
                    sx={{
                      bgcolor: platform.color,
                      "&:hover": {
                        bgcolor: platform.color,
                        filter: "brightness(90%)",
                      },
                      py: 1.2,
                    }}
                  >
                    {platform.name}
                  </Button>
                </Grid>
              ))}
          </Grid>

          <Box
            sx={{
              textAlign: "center",
              mt: 3,
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {(() => {
              const branding = getBranding(eventId);
              return `Powered by ${branding.companyName}`;
            })()}
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default SocialSharePage;
